import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { convertToNumber } from "@app/utils/functions";
import { useEquipment, useUser } from "@services/servicesGenericsTables";
import React, { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

const MeasurementSR = () => {
  const { watch, setValue, getValues } = useFormContext();
  const { currentMeasurement } = useAnalysis();
  const { userList } = useUser();
  const { equipmentList } = useEquipment();
  const filterSamplingProcedureList = useFilteredProcedures("AA");

  const [validPreparation, setValidPreparation] = useState<
    string | null | undefined
  >(undefined);

  const watchSrAaMgL = convertToNumber(watch("sr_aa_mg_l") || null);
  const carrierConcentration = watch("carrier_concentration") || 0;
  const preparationPerformance = watch("preparation_performance") || 0;

  useEffect(() => {
    if (watchSrAaMgL !== undefined && watchSrAaMgL !== null) {
      const calculatedSrAaMg = watchSrAaMgL * 12.5;
      const calculatePerformance = carrierConcentration
        ? calculatedSrAaMg / carrierConcentration
        : 0;

      const calculateValidMeasure =
        Math.abs((calculatePerformance / preparationPerformance) - 1);
      const isValidMeasure =
        isNaN(calculateValidMeasure) || calculatePerformance < 0
          ? null
          : calculateValidMeasure <= 0.2;
      const resultToCalculation =
        calculatePerformance < preparationPerformance
          ? calculatePerformance
          : Number(preparationPerformance);
      const calculatedSrCo3Aa = calculatedSrAaMg * 1.684744;

      setValue(
        "sr_aa_mg",
        calculatedSrAaMg ? calculatedSrAaMg.toFixed(2) : "0.00"
      );
      setValue(
        "measurement_performance",
        calculatePerformance ? calculatePerformance.toFixed(3) : "0.000"
      );
      const isValidPreparation =
        isValidMeasure === null ? null : isValidMeasure ? "S" : "N";
      setValue("valid_measure", isValidPreparation);
      setValue(
        "result_to_calculation",
        typeof resultToCalculation === "number"
          ? resultToCalculation.toFixed(3)
          : "0.00"
      );
      setValidPreparation(isValidPreparation);
      setValue(
        "sr_co3_aa",
        calculatedSrCo3Aa ? calculatedSrCo3Aa.toFixed(2) : "0.00"
      );
    } else {
      setValue("sr_aa_mg", "0.00");
      setValue("measurement_performance", "0.000");
      setValue("valid_measure", null);
      setValidPreparation(undefined);
    }
  }, [watchSrAaMgL]); // eslint-disable-line react-hooks/exhaustive-deps

  const stylesInput = useMemo(() => {
    if (validPreparation === "N") {
      return { backgroundColor: "#ef233c" };
    } else if (validPreparation === "S") {
      return { backgroundColor: "#a7c957" };
    }
    return { backgroundColor: "#fff" };
  }, [validPreparation]);

  return (
    <>
      {currentMeasurement.index !== undefined ? (
        <React.Fragment key={currentMeasurement.id}>
          <ResponsiveFormContainer columns={4}>
            <MAutocomplete
              name="measurement_analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
              disabled={!currentMeasurement.canEdit}
            />
            <MAutocomplete
              key={JSON.stringify(
                getValues("measurement_processing_procedure")
              )}
              name="measurement_processing_procedure"
              options={filterSamplingProcedureList}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
              disabled={!currentMeasurement.canEdit}
            />
            <MDatePicker
              name="measurement_date_time"
              pickerType="date"
              label="Fecha Inicio"
              disabled={!currentMeasurement.canEdit}
            />
            <MAutocomplete
              key={JSON.stringify(getValues("measurement_equipment"))}
              name="measurement_equipment"
              options={equipmentList}
              label="Seleccione un Equipo"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              multiple={true}
              disabled={!currentMeasurement.canEdit}
            />
            <MTextField
              name="sr_aa_mg_l"
              label="Sr AA (mg/l)"
              placeholder="Sr AA (mg/l)"
              fullWidth
              step={0.01}
              disabled={!currentMeasurement.canEdit}
            />

            <MTextField
              key={JSON.stringify(watch("sr_aa_mg"))}
              name="sr_aa_mg"
              label="Sr AA (mg)"
              placeholder="Sr AA (mg)"
              fullWidth
              step={0.01}
              disabled
            />
            <MTextField
              key={JSON.stringify(watch("measurement_performance"))}
              name="measurement_performance"
              label="Rto. Sr AA (sin unidad)"
              placeholder="Rto. Sr AA (sin unidad)"
              fullWidth
              step={0.001}
              disabled
            />
            <MTextField
              name="sr_co3_aa"
              label="SrCO3 AA (mg)"
              placeholder="SrCO3 AA (mg)"
              fullWidth
              step={0.01}
              disabled
            />

            <MTextField
              name="valid_measure"
              label="Medida Válida"
              placeholder="Medida Válida"
              value={validPreparation}
              fullWidth
              disabled
              style={stylesInput}
            />
            <MTextField
              name="result_to_calculation"
              label="Resultado que se toma para el cálculo de actividad"
              placeholder="Medida Válida"
              type="text"
              disabled
              fullWidth
              step={0.01}
            />
          </ResponsiveFormContainer>
        </React.Fragment>
      ) : null}
    </>
  );
};

export default MeasurementSR;
